<script>
/**
 * @component DashboardAlerts
 * @description Tablero de seguimiento rediseñado (UC-REG-065).
 *              Layout: KPIs row + bloque "Alertas críticas" (cards) + bloque
 *              "Alertas resto" (lista compacta) + bloque "Pacientes" (tabla).
 *
 *              Mantiene lógica de filtros + paginación + búsqueda del viejo.
 *              Reemplaza la grilla legacy por sub-componentes nuevos:
 *              `AlertaCriticaCard.vue`, `AlertasListaCompacta.vue`,
 *              `PacientesTabla.vue`.
 *
 *              Las "alertas críticas" se computan en frontend con
 *              `isCriticalAlert()` del servicio monitoring desde la última
 *              healthConversation del paciente. Si la org no usa
 *              healthConversation, el bloque queda vacío y se oculta.
 */
import Spinner from "@/components/ui/Spinner";
import TrackingNav from "@/components/ui/TrackingNav.vue";
import patientService from "@/services/patient";
import userService from "@/services/user";
import helperService from "@/services/helper";
import episodeService from "@/services/episode";
import pagination from "@/mixins/pagination";
import { EVENTS_TYPES } from "@/services/constants";
import { USER_ROLES } from "@/services/constants";
import { mapGetters } from "vuex";
import { isProtocolsEnabled } from "@/services/config";
import debounce from "debounce";
import { differenceInHours, parseISO } from "date-fns";
import moment from "moment";
import { orderBy } from "lodash";

import AlertaCriticaCard from "@/components/dashboard/AlertaCriticaCard.vue";
import AlertasListaCompacta from "@/components/dashboard/AlertasListaCompacta.vue";
import PacientesTabla from "@/components/dashboard/PacientesTabla.vue";
import monitoringService, { isCriticalAlert } from "@/services/monitoring";

const DEBOUNCE_TIMEOUT_MS = 500;

export default {
  name: "DashboardAlerts",

  mixins: [pagination],

  components: {
    Spinner,
    TrackingNav,
    AlertaCriticaCard,
    AlertasListaCompacta,
    PacientesTabla,
  },

  data() {
    return {
      EVENTS_TYPES,
      patients: [],
      filteredPatients: [],
      doctors: {},

      // Alertas hidratadas (desde monitoringService por paciente) — combinadas
      // de varios pacientes para construir el bloque de "Alertas urgentes"
      hydratedAlerts: [],
      isLoadingHydration: false,

      isProtocolsEnabled,
      isLoading: false,
      isLoadingDoctors: false,
      filterByActiveTreatment: true,

      searchInput: "",
      searchQuery: {},

      query: {
        status: "",
        measurement: "",
        doctor: "",
      },

      measurementOptions: [
        { text: "Rojas", value: "red" },
        { text: "Amarillas", value: "yellow" },
        { text: "Verdes", value: "green" },
        { text: "Sin Revisar", value: "requiresVerification" },
        { text: "Incompletas", value: "incomplete" },
      ],

      // Filtro local del bloque alertas
      alertsFilter: "all", // 'all' | 'red' | 'yellow' | 'mine'

      // Overwrite pagination mixin
      pagination: {
        limit: 100,
      },
    };
  },

  created() {
    this.getPatients();
    if (!this.isDoctor) {
      this.getDoctors();
    }
  },

  computed: {
    ...mapGetters(["isDoctor"]),

    statusOptions() {
      const filteredObject = {};
      Object.keys(this.EVENTS_TYPES).forEach((key) => {
        if (key !== "closed") {
          filteredObject[key] = this.EVENTS_TYPES[key];
        }
      });
      return {
        ...filteredObject,
        newPatient: { friendlyName: "Paciente nuevo", status: "new-patient" },
      };
    },

    /** @returns {number} Total de pacientes (visibles según filtros) */
    statTotal() {
      return this.filteredPatients.length;
    },

    /** @returns {number} Pacientes con al menos 1 alerta roja */
    statPatientsRed() {
      return this.filteredPatients.filter(
        (p) => (p.patient?.aggregates?.red || 0) > 0
      ).length;
    },

    /** @returns {number} Pacientes con amarillas pero no rojas */
    statPatientsYellow() {
      return this.filteredPatients.filter((p) => {
        const ag = p.patient?.aggregates || {};
        return (ag.yellow || 0) > 0 && (ag.red || 0) === 0;
      }).length;
    },

    /** @returns {number} Pacientes sin alertas (todo verde o sin tasks rojo/amarillo) */
    statPatientsGreen() {
      return this.filteredPatients.filter((p) => {
        const ag = p.patient?.aggregates || {};
        return !ag.red && !ag.yellow;
      }).length;
    },

    /** @returns {number} Pacientes que requieren verificación (sin revisar últimas 24h) */
    statPendingReview() {
      return this.filteredPatients.filter((p) =>
        this.doesPatientRequiresVerification(p)
      ).length;
    },

    /** @returns {number} Pacientes sin contacto últimas 72h */
    statWithoutContact() {
      const now = new Date();
      return this.filteredPatients.filter((p) => {
        const at = p.patient?.aggregates?.updatedAt;
        if (!at) return true;
        const hours = (now - new Date(at)) / 1000 / 60 / 60;
        return hours >= 72;
      }).length;
    },

    /**
     * @description Alertas críticas computadas — rojo + score≥9 o keyword crítica.
     *              Solo subset de hydratedAlerts con isCriticalAlert true.
     */
    criticalAlerts() {
      return this.hydratedAlerts
        .filter((a) => isCriticalAlert(a))
        .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
    },

    /**
     * @description Resto de alertas no críticas para la lista compacta.
     *              Aplica el filtro local (alertsFilter).
     */
    nonCriticalAlerts() {
      const criticalIds = new Set(this.criticalAlerts.map((a) => a._id));
      let alerts = this.hydratedAlerts.filter(
        (a) =>
          !criticalIds.has(a._id) &&
          (a.completedMeasurement === "red" ||
            a.completedMeasurement === "yellow")
      );
      if (this.alertsFilter === "red") {
        alerts = alerts.filter((a) => a.completedMeasurement === "red");
      } else if (this.alertsFilter === "yellow") {
        alerts = alerts.filter((a) => a.completedMeasurement === "yellow");
      }
      // Sort: rojas primero, después por timestamp desc
      return alerts.sort((a, b) => {
        if (a.completedMeasurement !== b.completedMeasurement) {
          return a.completedMeasurement === "red" ? -1 : 1;
        }
        return new Date(b.completedAt) - new Date(a.completedAt);
      });
    },
  },

  watch: {
    filterByActiveTreatment() {
      this.filteredPatients = this.filterByActiveTreatment
        ? this.patients.filter((patient) => patient.assignedTreatments.length)
        : this.patients;
      this.pagination.total = this.filteredPatients.length;
    },
    // ─── Comentado 2026-05-30 junto con el bloque alertas (ver template).
    // Si se re-activan los bloques de alertas, descomentar este watcher
    // para que vuelva a hidratar la data del bloque.
    // filteredPatients() {
    //   this.hydrateAlertsForPatients();
    // },
  },

  methods: {
    getPatients() {
      this.isLoading = true;

      const query = {};
      this.query.status && (query.status = this.query.status);
      this.query.measurement && (query.measurement = this.query.measurement);
      this.query.doctor && (query.doctors = this.query.doctor);

      const aggregationQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        ...query,
      };

      if (this.searchInput) {
        Object.assign(aggregationQuery, this.searchQuery);
      }

      patientService
        .getAggregates({ params: aggregationQuery })
        .then((data) => {
          if (!data || !data.docs) return [];
          return data.docs;
        })
        .then(async (res) => {
          if (res.length) {
            this.patients = await Promise.all(
              res.map(async (patient) => {
                const episodeRes = await episodeService.get(patient._id);
                const episodes = episodeRes.docs;
                const activeEpisode = episodes.find(
                  ({ status }) => status === "active"
                );

                patient.assignedTreatments = activeEpisode
                  ? orderBy(
                      (activeEpisode.treatments || [])
                        .filter((treatment) => !treatment.unnasignedAt)
                        .map((treatment) => ({
                          ...treatment,
                          length: moment(treatment.lastTaskScheduledAt).diff(
                            moment(treatment.firstTaskScheduledAt)
                          ),
                        })),
                      "length",
                      ["desc"]
                    )
                  : [];

                return patient;
              })
            );

            this.filteredPatients = this.filterByActiveTreatment
              ? this.patients.filter(
                  (patient) => patient.assignedTreatments.length
                )
              : this.patients;
            this.pagination.total = this.filteredPatients.length;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    /**
     * @description Hidrata las alertas críticas/no-críticas pidiendo al backend
     *              el monitoring de los pacientes filtrados que tengan alguna
     *              alerta (rojo o amarillo). Concurrency 5.
     */
    async hydrateAlertsForPatients() {
      const candidates = this.filteredPatients.filter((p) => {
        const ag = p.patient?.aggregates || {};
        return (ag.red || 0) > 0 || (ag.yellow || 0) > 0;
      });
      if (!candidates.length) {
        this.hydratedAlerts = [];
        return;
      }
      this.isLoadingHydration = true;
      const collected = [];
      const queue = [...candidates];
      const workers = Array.from({ length: 5 }, async () => {
        while (queue.length > 0) {
          const p = queue.shift();
          if (!p) return;
          try {
            const { data } = await monitoringService.getPatientMonitoring(
              p._id
            );
            const lastConv = data.lastConversation;
            if (
              lastConv &&
              lastConv.semaphore &&
              lastConv.semaphore !== "green"
            ) {
              collected.push({
                _id: `${p._id}-conv-${lastConv.taskId}`,
                patientId: p._id,
                patientName: `${p.firstName} ${p.lastName}`,
                programLabel: p.assignedTreatments[0]?.treatment?.name,
                type: "health-conversation",
                taskName: lastConv.name,
                completedMeasurement: lastConv.semaphore,
                score: lastConv.score,
                signals: lastConv.topSignals || [],
                signalSnippet: (lastConv.topSignals || [])
                  .map((s) => s.value)
                  .filter(Boolean)
                  .join(" · "),
                aiJustification: lastConv.aiJustification,
                turnsUsed: lastConv.turnsUsed,
                completedAt: lastConv.at,
                hasRisk: false, // se computa en isCriticalAlert
              });
            }
            // Sumar otras alertas no IA: vitales rojos, medicaciones no completadas, etc.
            // Para MVP usamos la activity con semaphore != null y semaphore != 'green'
            (data.activity || [])
              .filter((ev) => ev.semaphore && ev.semaphore !== "green")
              .slice(0, 3) // máx 3 alertas no-IA por paciente
              .forEach((ev) => {
                if (ev.type === "HealthConversationTask") return; // ya incluida arriba
                collected.push({
                  _id: `${p._id}-${ev.type}-${ev.payload.taskId}`,
                  patientId: p._id,
                  patientName: `${p.firstName} ${p.lastName}`,
                  programLabel: p.assignedTreatments[0]?.treatment?.name,
                  type:
                    ev.type === "VitalTask"
                      ? "vital"
                      : ev.type === "CheckinTask"
                      ? "checkin"
                      : ev.type === "MedicationTask"
                      ? "medication"
                      : ev.type,
                  taskName: ev.payload.name,
                  completedMeasurement: ev.semaphore,
                  value: ev.payload.value
                    ? `${ev.payload.value}${
                        ev.payload.unit ? " " + ev.payload.unit : ""
                      }`
                    : null,
                  completedAt: ev.at,
                });
              });
          } catch (e) {
            // best-effort, no rompemos UI por un fallo de hidratación
          }
        }
      });
      await Promise.all(workers);
      this.hydratedAlerts = collected;
      this.isLoadingHydration = false;
    },

    goToPatientDashboard(id) {
      // tab=info → aterrizar directo en "Seguimiento del paciente" en vez
      // de "Historia clínica" (default del PatientDashboard). El target
      // lee este query param en created y cambia dashboardTab.
      helperService.callIfNoSelectedText(() =>
        this.$router.push({
          name: "patient-dashboard",
          params: { id },
          query: { tab: "info" },
        })
      );
    },

    onAtenderAlerta(alert) {
      this.goToPatientDashboard(alert.patientId);
    },

    onMarcarAlerta(alert) {
      // MVP: solo lo saca de la lista local. Persistir "visto" es un próximo paso.
      this.hydratedAlerts = this.hydratedAlerts.filter(
        (a) => a._id !== alert._id
      );
    },

    onVerPaciente(patient) {
      this.goToPatientDashboard(patient._id);
    },

    async getDoctors() {
      this.isLoadingDoctors = true;
      try {
        const users = await userService.get({
          roles: [USER_ROLES.DOCTOR.id],
          select: ["firstName", "lastName"],
        });
        this.doctors = users.docs.reduce((acc, doctor) => {
          acc[doctor._id] = doctor;
          return acc;
        }, {});
      } finally {
        this.isLoadingDoctors = false;
      }
    },

    onSearch: debounce(function () {
      if (!this.searchInput) {
        this.searchQuery = {};
        this.getPatients();
        return;
      }
      const input = this.searchInput.split(" ");
      const useSearchIndex = input && (input.length === 1 || input.length > 3);
      const useNameIndex = input && input.length > 1 && input.length <= 3;
      if (useSearchIndex) {
        this.searchQuery = { $text: { $search: this.searchInput } };
        this.getPatients();
        return;
      } else if (useNameIndex) {
        Object.assign(
          this.searchQuery,
          helperService.createNameQuery(this.searchInput)
        );
      }
      this.getPatients();
    }, DEBOUNCE_TIMEOUT_MS),

    onPageChange() {
      this.getPatients();
    },

    doesPatientRequiresVerification(user) {
      const { lastEvent } = user.patient;
      const { lastEventCreatedAt } = user.patient;
      if (!lastEvent || !lastEventCreatedAt) return true;
      return differenceInHours(new Date(), parseISO(lastEventCreatedAt)) >= 24;
    },
  },
};
</script>

<template lang="pug">
  .dashboard-alerts-v2
    //- UC-REG-094: barra de tabs Seguimientos / Protocolos / Configuración.
    //- Federico aclaró que Protocolos NO es un programa en sí mismo sino el
    //- configurador de Seguimientos. La barra antes solo aparecía en
    //- /treatment; la trasladamos también acá para que el usuario navegue
    //- entre las 3 pestañas desde donde sea. Por eso reemplaza el item
    //- "Protocolos" que UC-REG-090 había puesto en la sidebar (revertido).
    TrackingNav(active="seguimientos")
    //- KPIs row (6 cards)
    .kpis-row
      .kpi
        .kpi__label Total pacientes
        .kpi__value {{ statTotal }}
        .kpi__delta activos en la cartera
      .kpi.kpi--red
        .kpi__label En rojo
        .kpi__value {{ statPatientsRed }}
        .kpi__delta &nbsp;
      .kpi.kpi--yellow
        .kpi__label Amarillo
        .kpi__value {{ statPatientsYellow }}
        .kpi__delta &nbsp;
      .kpi.kpi--green
        .kpi__label Sin alertas
        .kpi__value {{ statPatientsGreen }}
        .kpi__delta &nbsp;
      .kpi
        .kpi__label Pending review
        .kpi__value {{ statPendingReview }}
        .kpi__delta sin revisar 24h
      .kpi
        .kpi__label Sin contacto
        .kpi__value {{ statWithoutContact }}
        .kpi__delta &gt; 72h

    //- Filtros globales (acotados al MVP)
    .filters-bar
      el-input(
        v-model="searchInput"
        size="medium"
        placeholder="🔍 Buscar paciente..."
        clearable
        @input="onSearch"
        style="max-width: 280px"
      )
      el-select(
        v-if="!isDoctor"
        v-model="query.doctor"
        size="medium"
        clearable
        placeholder="Médico"
        @change="getPatients"
        style="width: 200px"
      )
        el-option(
          v-for="doctor in doctors"
          :key="doctor._id"
          :value="doctor._id"
          :label="`${doctor.firstName} ${doctor.lastName}`"
        )
      el-select(
        v-model="query.measurement"
        size="medium"
        clearable
        placeholder="Estado"
        @change="getPatients"
        style="width: 160px"
      )
        el-option(
          v-for="opt in measurementOptions"
          :key="opt.value"
          :value="opt.value"
          :label="opt.text"
        )
      el-checkbox(v-model="filterByActiveTreatment") Solo activos

    //- ─────────────────────────────────────────────────────────────────────
    //- BLOQUES DE ALERTAS — comentados 2026-05-30
    //- Federico marcó como redundante con la tabla de Pacientes (que ya
    //- muestra alertas count + signals top + score IA). Mantenemos el
    //- código + lógica para re-activar fácil si decidimos volver atrás:
    //- - quitar el v-if="false" del wrapper de abajo
    //- - el resto del componente (computeds criticalAlerts, nonCriticalAlerts,
    //-   hydrateAlertsForPatients, watcher, métodos) sigue funcionando
    //- - import de AlertaCriticaCard/AlertasListaCompacta sigue activo
    //- ─────────────────────────────────────────────────────────────────────
    template(v-if="false")
      //- Bloque ALERTAS CRÍTICAS (cards) — solo si hay
      .block(v-if="criticalAlerts.length > 0")
        .block__header
          span.block__title ⚠ Alertas críticas
          span.block__count {{ criticalAlerts.length }}
        .alerts-grid
          AlertaCriticaCard(
            v-for="alert in criticalAlerts"
            :key="alert._id"
            :alert="alert"
            @atender="onAtenderAlerta"
            @marcar="onMarcarAlerta"
          )

      //- Bloque ALERTAS RESTO (lista compacta)
      .block(v-if="!isLoading && (nonCriticalAlerts.length > 0 || hydratedAlerts.length > 0)")
        .block__header
          span.block__title Alertas urgentes
        .block__filters
          el-radio-group(v-model="alertsFilter" size="mini")
            el-radio-button(label="all") Todas
            el-radio-button(label="red") Rojas
            el-radio-button(label="yellow") Amarillas
        AlertasListaCompacta(
          :alerts="nonCriticalAlerts"
          @atender="onAtenderAlerta"
          @marcar="onMarcarAlerta"
        )

    //- Bloque PACIENTES (tabla)
    .block
      .block__header
        span.block__title Pacientes
        span.block__count {{ filteredPatients.length }}
      Spinner(v-if="isLoading")
      PacientesTabla(
        v-else
        :patients="filteredPatients"
        @ver="onVerPaciente"
      )
</template>

<style lang="scss" scoped>
.dashboard-alerts-v2 {
  font-family: "Plus Jakarta Sans", sans-serif;
  padding: 20px;
  background: #f4f6fb;
  min-height: calc(100vh - 64px);
}

.kpis-row {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}
.kpi {
  background: white;
  border-radius: 12px;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  &__label {
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
    margin-bottom: 4px;
  }
  &__value {
    font-size: 28px;
    font-weight: 700;
    color: #0f1e36;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
  }
  &__delta {
    font-size: 11px;
    color: #9ca3af;
    margin-top: 4px;
  }
  &--red &__value {
    color: #b3261e;
  }
  &--yellow &__value {
    color: #b07c0a;
  }
  &--green &__value {
    color: #1f8a4c;
  }
}

.filters-bar {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
  background: white;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.block {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  margin-bottom: 16px;
  overflow: hidden;

  &__header {
    padding: 14px 20px;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #0f1e36;
  }
  &__count {
    font-size: 12px;
    color: #6b7280;
    background: #f3f4f6;
    padding: 2px 8px;
    border-radius: 10px;
    font-weight: 600;
  }
  &__filters {
    padding: 12px 20px;
    border-bottom: 1px solid #e5e7eb;
    background: #fbfcfe;
  }
}

.alerts-grid {
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
